import { v4 as uuid } from 'uuid';

import { RouteManager } from '../../../RouteManager';
import { ToastManager } from '../../../ToastManager';
import type { CommandItem, CommandProviderArgs } from '../command-provider.types';
import { COMMAND_SECTIONS } from '../command-types.ts';

import type { AdvancedFlowProps } from '../../../store/advanced-flow/AdvancedFlowsStore.ts';
import type { FlowFoldersStoreState } from '../../../store/flow-folders/FlowFoldersStore.ts';
import type { FlowProps } from '../../../store/flow/FlowsStore.ts';
import { userMeDraftStore } from '../../../store/users/UserMeDraftStore.ts';
import type { UserProps } from '../../../store/users/UserMeStore';

import type { useApi } from '../../../store/SelectedHomeyStore.hooks';

import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';

import { asStringArray, copyToClipboard, isMatch, normalize, toastAdd } from './provider-utils.ts';
import { runNewAdvancedFlow } from './static-provider';

type FlowProviderApi = ReturnType<typeof useApi>['api'];
type FlowProviderI18n = CommandProviderArgs['i18n'];

type UserMeWithFavorites = {
  properties?: {
    favoriteFlows?: string[];
  };
};

async function triggerFlow({
  flow,
  isAdvancedFlow,
  api,
  i18n,
}: {
  flow: FlowProps | AdvancedFlowProps;
  isAdvancedFlow: boolean;
  api: FlowProviderApi;
  i18n: FlowProviderI18n;
}): Promise<void> {
  if (flow.triggerable !== true || api == null) {
    return;
  }

  if (isAdvancedFlow === true) {
    await api.flow.triggerAdvancedFlow({
      id: flow.id,
      state: {},
    });
    toastAdd({
      icon: glyph.system.advancedFlow,
      message: i18n.messageFormatter('flow.startedAdvancedFlow', { name: flow.name }),
    });
    return;
  }

  await api.flow.triggerFlow({ id: flow.id });
  toastAdd({
    icon: glyph.system.flow,
    message: i18n.messageFormatter('flow.started', { name: flow.name }),
  });
}

async function toggleFavoriteFlow({
  flow,
  userMe,
  api,
  i18n,
}: {
  flow: FlowProps | AdvancedFlowProps;
  userMe: UserMeWithFavorites;
  api: FlowProviderApi;
  i18n: FlowProviderI18n;
}): Promise<void> {
  if (flow == null || userMe == null || api == null) {
    return;
  }
  const favoriteName = `**${flow.name}**`;
  const currentFavoriteFlows = asStringArray(userMe?.properties?.favoriteFlows);
  const isFavoriteFlow = currentFavoriteFlows.includes(flow.id);
  const nextFavoriteFlows =
    isFavoriteFlow === true
      ? currentFavoriteFlows.filter((id) => {
          return id !== flow.id;
        })
      : currentFavoriteFlows.includes(flow.id)
        ? currentFavoriteFlows
        : [...currentFavoriteFlows, flow.id];

  const nextUserMe = {
    ...userMe,
    properties: {
      ...userMe.properties,
      favoriteFlows: nextFavoriteFlows,
    },
  };

  try {
    userMeDraftStore.setUserMeDraft({ userMe: nextUserMe as unknown as UserProps });

    await api.users.updateUserMeProperties?.({
      id: 'favoriteFlows',
      value: nextFavoriteFlows,
    });

    toastAdd({
      icon: isFavoriteFlow === true ? glyph.core.heartOutline : glyph.core.heartFill,
      iconColor: isFavoriteFlow === true ? theme.icon.color_light : theme.color.special_favorite,
      message: i18n.messageFormatter(
        isFavoriteFlow === true ? 'flow.removeFavorite' : 'flow.addFavorite',
        { name: favoriteName },
      ),
    });
    userMeDraftStore.setUserMeDraftDone();
  } catch (error) {
    userMeDraftStore.setUserMeDraftFailed();
    ToastManager.handleError(error);
  }
}

function getFlowFolderName(
  flow: FlowProps | AdvancedFlowProps,
  flowFoldersById: FlowFoldersStoreState['byId'],
  i18n: FlowProviderI18n,
): string {
  const folderSource = flow.folder;

  if (folderSource == null) {
    return i18n.messageFormatter('commandPalette.flows.rootFolder');
  }

  return flowFoldersById?.[folderSource]?.name ?? '';
}

export function getFlowCommands(args: CommandProviderArgs): CommandItem[] {
  const { query, activeContext, flowsById, advancedFlowsById, flowFoldersById, api, i18n, userMe } =
    args;
  const openTitle = i18n.messageFormatter('commandPalette.actions.open');
  const copyIdTitle = i18n.messageFormatter('commandPalette.actions.copyId');
  const copyJsonTitle = i18n.messageFormatter('commandPalette.actions.copyJson');

  const isFlowsScopeContext =
    activeContext?.type === 'scope' && activeContext.scopeType === 'flows';
  const scopeCommands = isFlowsScopeContext
    ? [
        {
          key: 'cmd:scope:flows:open',
          kind: 'action',
          section: COMMAND_SECTIONS.NAVIGATION,
          sortOrder: 10,
          title: openTitle,
          subtitle: i18n.messageFormatter('commandPalette.actions.flowsPage'),
          icon: glyph.core.squareArrowUpRight,
          keywords: ['open', 'flows', 'go to flows'],
          getOpenInNewTabPath() {
            return RouteManager.getPathForFlows();
          },
          onRun() {
            RouteManager.toFlows();
          },
        },
        {
          key: 'cmd:scope:flows:new-flow',
          kind: 'action',
          section: COMMAND_SECTIONS.CONTEXT_ACTIONS,
          sortOrder: 20,
          title: i18n.messageFormatter('header.action.flow'),
          subtitle: i18n.messageFormatter('commandPalette.actions.createStandardFlow'),
          icon: glyph.system.flow,
          keywords: ['new flow', 'create flow'],
          onRun() {
            RouteManager.toFlow(`create?id=${uuid()}`);
          },
        },
        {
          key: 'cmd:scope:flows:new-advanced-flow',
          kind: 'action',
          section: COMMAND_SECTIONS.CONTEXT_ACTIONS,
          sortOrder: 30,
          title: i18n.messageFormatter('header.action.advancedFlow'),
          subtitle: i18n.messageFormatter('commandPalette.actions.createAdvancedFlow'),
          icon: glyph.system.advancedFlow,
          keywords: ['new advanced flow', 'create advanced flow'],
          onRun() {
            runNewAdvancedFlow({ api });
          },
        },
      ]
    : [];

  if (activeContext?.type === 'flow') {
    const sourceById = activeContext.isAdvancedFlow === true ? advancedFlowsById : flowsById;
    const flowOrAdvancedFlow =
      activeContext.flowId != null ? (sourceById?.[activeContext.flowId] ?? null) : null;
    const isTriggerable = flowOrAdvancedFlow?.triggerable === true && api != null;

    if (flowOrAdvancedFlow == null) {
      return [];
    }

    const userMeTyped = (userMe ?? {}) as UserMeWithFavorites;
    const isFavoriteFlow = asStringArray(userMeTyped?.properties?.favoriteFlows).includes(
      flowOrAdvancedFlow.id,
    );

    return [
      {
        key: `cmd:flow:${flowOrAdvancedFlow.id}:open`,
        kind: 'action',
        section: COMMAND_SECTIONS.CONTEXT_NAVIGATE,
        sortOrder: 10,
        title: openTitle,
        subtitle: flowOrAdvancedFlow.name,
        icon: glyph.core.squareArrowUpRight,
        keywords: ['open', 'flow page', 'go to flow'],
        getOpenInNewTabPath() {
          if (activeContext.isAdvancedFlow === true) {
            return RouteManager.getPathForAdvancedFlow(flowOrAdvancedFlow.id);
          }

          return RouteManager.getPathForFlow(flowOrAdvancedFlow.id);
        },
        onRun() {
          if (activeContext.isAdvancedFlow === true) {
            RouteManager.toAdvancedFlow(flowOrAdvancedFlow.id);
            return;
          }

          RouteManager.toFlow(flowOrAdvancedFlow.id);
        },
      },
      {
        key: `cmd:flow:${flowOrAdvancedFlow.id}:start`,
        kind: 'action',
        section: COMMAND_SECTIONS.CONTEXT_ACTIONS,
        sortOrder: 20,
        title: i18n.messageFormatter('flow.start'),
        subtitle:
          isTriggerable === true
            ? flowOrAdvancedFlow.name
            : i18n.messageFormatter('flow.notTriggerable'),
        icon: glyph.core.play,
        keywords: ['start', 'run', 'trigger', 'test'],
        isDisabled: isTriggerable !== true,
        keepOpenOnShiftEnter: isTriggerable === true,
        async onRun() {
          await triggerFlow({
            flow: flowOrAdvancedFlow,
            isAdvancedFlow: activeContext.isAdvancedFlow === true,
            api,
            i18n,
          });
        },
      },
      ...(userMe == null
        ? []
        : [
            {
              key: `cmd:flow:${flowOrAdvancedFlow.id}:favorite`,
              kind: 'action',
              section: COMMAND_SECTIONS.CONTEXT_ACTIONS,
              sortOrder: 25,
              title: i18n.messageFormatter('flow.favorite'),
              subtitle: flowOrAdvancedFlow.name,
              icon: isFavoriteFlow === true ? glyph.core.heartFill : glyph.core.heartOutline,
              keywords: ['favorite', 'unfavorite', 'heart'],
              async onRun() {
                await toggleFavoriteFlow({
                  flow: flowOrAdvancedFlow,
                  userMe: userMeTyped,
                  api,
                  i18n,
                });
              },
            },
          ]),
      {
        key: `cmd:flow:${flowOrAdvancedFlow.id}:copy-id`,
        kind: 'action',
        section: COMMAND_SECTIONS.CONTEXT_DEVELOPER,
        sortOrder: 30,
        title: copyIdTitle,
        subtitle: flowOrAdvancedFlow.name,
        icon: glyph.core.squareOnSquare,
        keywords: ['copy', 'flow id', 'id'],
        onRun() {
          return copyToClipboard({ value: flowOrAdvancedFlow.id, icon: glyph.core.squareOnSquare });
        },
      },
      {
        key: `cmd:flow:${flowOrAdvancedFlow.id}:copy-json`,
        kind: 'action',
        section: COMMAND_SECTIONS.CONTEXT_DEVELOPER,
        sortOrder: 40,
        title: copyJsonTitle,
        subtitle: flowOrAdvancedFlow.name,
        icon: glyph.core.squareOnSquare,
        keywords: ['copy', 'json', 'flow json'],
        onRun() {
          return copyToClipboard({
            value: JSON.stringify(flowOrAdvancedFlow, null, 2),
            icon: glyph.core.squareOnSquare,
          });
        },
      },
    ];
  }

  if (activeContext?.type === 'device') {
    return [];
  }

  if (activeContext?.type === 'scope' && activeContext.scopeType === 'devices') {
    return [];
  }

  if (activeContext?.type === 'scope' && activeContext.scopeType === 'settings') {
    return [];
  }

  if (activeContext?.type === 'scope' && activeContext.scopeType === 'logs') {
    return [];
  }

  if (activeContext?.type === 'app') {
    return [];
  }

  const normalizedQuery = normalize(query.trim());

  if (normalizedQuery === '' && isFlowsScopeContext !== true) {
    return [];
  }

  const commands: CommandItem[] = [...scopeCommands];

  for (const flow of Object.values(flowsById ?? {})) {
    appendFlowEntityCommand({
      commands,
      flowOrAdvancedFlow: flow,
      isAdvanced: false,
      flowFoldersById,
      i18n,
      normalizedQuery,
    });
  }

  for (const advancedFlow of Object.values(advancedFlowsById ?? {})) {
    appendFlowEntityCommand({
      commands,
      flowOrAdvancedFlow: advancedFlow,
      isAdvanced: true,
      flowFoldersById,
      i18n,
      normalizedQuery,
    });
  }

  return commands;
}

function appendFlowEntityCommand({
  commands,
  flowOrAdvancedFlow,
  isAdvanced,
  flowFoldersById,
  i18n,
  normalizedQuery,
}: {
  commands: CommandItem[];
  flowOrAdvancedFlow: FlowProps | AdvancedFlowProps;
  isAdvanced: boolean;
  flowFoldersById: FlowFoldersStoreState['byId'];
  i18n: FlowProviderI18n;
  normalizedQuery: string;
}): void {
  const folderName = getFlowFolderName(flowOrAdvancedFlow, flowFoldersById, i18n);

  if (
    isMatch(flowOrAdvancedFlow.name, normalizedQuery) !== true &&
    isMatch(folderName, normalizedQuery) !== true &&
    isMatch(flowOrAdvancedFlow.id, normalizedQuery) !== true
  ) {
    return;
  }

  commands.push({
    key: `${isAdvanced === true ? 'entity:advanced-flow' : 'entity:flow'}:${flowOrAdvancedFlow.id}`,
    kind: 'entity',
    section: COMMAND_SECTIONS.FLOWS,
    title: flowOrAdvancedFlow.name ?? flowOrAdvancedFlow.id,
    subtitle: folderName || undefined,
    icon: isAdvanced === true ? glyph.system.advancedFlow : glyph.system.flow,
    keywords: [flowOrAdvancedFlow.id, folderName].filter((value) => Boolean(value)),
    meta: {
      entityType: isAdvanced === true ? 'advancedFlow' : 'flow',
      entityId: flowOrAdvancedFlow.id,
      canEnterContextWithTab: true,
    },
    getOpenInNewTabPath() {
      if (isAdvanced === true) {
        return RouteManager.getPathForAdvancedFlow(flowOrAdvancedFlow.id);
      }

      return RouteManager.getPathForFlow(flowOrAdvancedFlow.id);
    },
    onRun() {
      if (isAdvanced === true) {
        RouteManager.toAdvancedFlow(flowOrAdvancedFlow.id);
        return;
      }

      RouteManager.toFlow(flowOrAdvancedFlow.id);
    },
  });
}
